import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageSearch, ChevronDown, Save, X, Loader2 } from "lucide-react";
import api, { formatMoney, formatMoney2, formatNumber } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useRowHover } from "./RowHover";

function MultiFiltro({ label, opciones, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const vis = opciones.filter((o) => o.nombre.toLowerCase().includes(q.toLowerCase()));
  const toggle = (n) => onChange(value.includes(n) ? value.filter((x) => x !== n) : [...value, n]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid}
                className={"inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors " +
                  (value.length ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400")}>
          {label}{value.length ? ` · ${value.length}` : ""} <ChevronDown size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-white p-2" align="start">
        <input className="input text-xs mb-2" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-buscar`} />
        <div className="max-h-64 overflow-y-auto">
          {vis.map((o) => (
            <label key={o.nombre} className="flex items-center gap-2 px-1.5 py-1 rounded text-xs hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={value.includes(o.nombre)} onChange={() => toggle(o.nombre)} data-testid={`${testid}-opt-${o.nombre}`} />
              <span className="flex-1 truncate">{o.nombre}</span><span className="text-slate-400">{o.n}</span>
            </label>
          ))}
          {!vis.length && <p className="text-xs text-slate-400 p-2">Sin resultados</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

export const ListaDReal = ({ importId }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [fam, setFam] = useState(null);
  const [lab, setLab] = useState(null);
  const [saving, setSaving] = useState(false);
  const hov = useRowHover();

  useEffect(() => {
    if (!importId) return;
    const params = fam === null ? { import_id: importId } : { import_id: importId, usar_guardado: false, familias: fam, laboratorios: lab };
    api.get("/informes/lista-d-real", { params, paramsSerializer: { indexes: null } }).then((r) => {
      setData(r.data);
      if (fam === null) { setFam(r.data.filtro.familias); setLab(r.data.filtro.laboratorios); }
    }).catch(() => setData(null));
  }, [importId, fam, lab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const total = data.productos.reduce((s, p) => s + (p.valor_pmc || 0), 0);
  const dirty = fam && (!same(fam, data.guardado.familias) || !same(lab, data.guardado.laboratorios));
  const save = async () => {
    setSaving(true);
    try {
      const { data: g } = await api.put("/informes/lista-d-real/filtro", { familias: fam, laboratorios: lab });
      setData({ ...data, guardado: g });
      toast.success(fam.length || lab.length ? "Filtro guardado: los PDF y emails llevarán esta lista" : "Los PDF y emails llevarán la lista completa");
    } catch { toast.error("No se pudo guardar el filtro"); } finally { setSaving(false); }
  };

  return (
    <div className="card p-4 sm:p-6 mb-6" data-testid="lista-d-real">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="lista-d-real-toggle">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><PackageSearch size={12} /> Para todo el equipo · {data.label}</div>
          <h3 className="font-display text-xl font-bold">Stock D real a recomendar</h3>
          <p className="text-xs text-slate-500 mt-1">
            {data.productos.length
              ? `${data.productos.length} productos con existencias, más de un año sin venderse y sin entradas · ${formatMoney(total)} parados a coste.`
              : data.tiene_estudio ? "No hay productos D real con existencias para este filtro." : "Este mes no tiene estudio de stock de referencia."}
            {data.pct > 0 && ` Cada venta da al empleado un ${String(data.pct_1).replace(".", ",")} % (1-2 años) o un ${String(data.pct_2).replace(".", ",")} % (más de 2 años) de lo vendido a PVP, según Empleados › Incentivo stock parado.`} Sale también en el PDF de cada empleado.
          </p>
        </div>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="lista-d-real-filtros">
            <MultiFiltro label="Familias" opciones={data.opciones.familias} value={fam || []} onChange={setFam} testid="lista-d-filtro-familias" />
            <MultiFiltro label="Laboratorios" opciones={data.opciones.laboratorios} value={lab || []} onChange={setLab} testid="lista-d-filtro-laboratorios" />
            {[...(fam || []).map((n) => ["f", n]), ...(lab || []).map((n) => ["l", n])].map(([t, n]) => (
              <span key={t + n} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px]">
                {n}<button onClick={() => (t === "f" ? setFam(fam.filter((x) => x !== n)) : setLab(lab.filter((x) => x !== n)))} aria-label={`Quitar ${n}`}><X size={11} /></button>
              </span>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[11px] text-slate-500" data-testid="lista-d-estado-filtro">
                {dirty ? "Cambios sin guardar: los PDF siguen con el filtro guardado" : data.guardado.familias.length || data.guardado.laboratorios.length ? "Este filtro es el que se envía" : "Se envía la lista completa"}
              </span>
              <button onClick={save} disabled={!dirty || saving} className="btn btn-primary text-xs py-1.5 disabled:opacity-50" data-testid="lista-d-guardar-filtro">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar para el envío
              </button>
            </div>
          </div>
          {data.productos.length > 0 && (
            <div className="overflow-x-auto mt-4">
              {hov.tip}
              <table className="dense" data-testid="lista-d-real-tabla">
                <thead><tr><th>CN</th><th>Producto</th><th>Familia</th><th>Laboratorio</th><th className="num">Exist.</th><th className="num">PVP</th><th className="num">Parado (coste)</th><th className="num">Uds vend.</th><th>Últ. venta</th><th>Últ. entrada</th></tr></thead>
                <tbody>
                  {data.productos.map((p, i) => (
                    <tr key={p.cn} data-testid={`lista-d-real-fila-${i}`} className="cursor-help" {...hov.bind(p)}>
                      <td className="font-mono text-xs">{p.cn}</td>
                      <td className="text-xs font-semibold">{p.descripcion}</td>
                      <td className="text-xs text-slate-500">{p.familia || "—"}</td>
                      <td className="text-xs text-slate-500">{p.laboratorio || "—"}</td>
                      <td className="num">{formatNumber(p.stock)}</td>
                      <td className="num">{formatMoney2(p.pvp)}</td>
                      <td className="num font-semibold">{formatMoney2(p.valor_pmc)}</td>
                      <td className="num">{formatNumber(p.uds_vendidas || 0)}</td>
                      <td className="text-xs">{p.u_salida ? new Date(p.u_salida).toLocaleDateString("es-ES") : "—"}</td>
                      <td className="text-xs">{p.u_entrada ? new Date(p.u_entrada).toLocaleDateString("es-ES") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
